import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraSpinner } from '@mono/ui-components/ensigns';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/authContext';
import { useTenantAreas } from '../../../lib/access/useTenantAreas';
import { RegistryRecordFrame } from '../RegistryRecordFrame';
import { currentFiscalYear } from './govorg/hooks';
import { AgainstTheAnchor } from './lcat/AgainstTheAnchor';
import { AnchorHeader } from './lcat/AnchorHeader';
import { anchorRail } from './lcat/anchorRail';
import { useLaborCategoryAnchor } from './lcat/hooks';
import { MapLaborCategoryDialog } from './lcat/MapLaborCategoryDialog';
import { PeopleWhoFilledIt } from './lcat/PeopleWhoFilledIt';
import { RateDotPlot } from './lcat/RateDotPlot';
import { VersionsTable } from './lcat/VersionsTable';
/**
 * Labor category anchor page, REG-19. Route /registries/labor-categories/:anchorId
 * behind registries_records_v1 (REG-08). Reads the REG-18 contract; people rows
 * never carry money and the rate trail is always restricted.
 */ export function LaborCategoryAnchorPage() {
    const { anchorId = '' } = useParams();
    const [params, setParams] = useSearchParams();
    const { user } = useAuth();
    const canMap = useTenantAreas().registries;
    const [mapOpen, setMapOpen] = useState(false);
    const [fy, setFy] = useState('current');
    const currentFy = currentFiscalYear();
    const years = fy === 'prev' ? [
        currentFy - 1
    ] : fy === 'current' ? [
        currentFy
    ] : [
        currentFy - 1,
        currentFy
    ];
    const queries = useLaborCategoryAnchor(anchorId, years);
    const dossier = queries.dossier.data;
    const status = queries.dossier.isPending ? 'loading' : queries.dossier.isError ? 'error' : dossier?.anchor ? 'ready' : 'unavailable';
    const selectedId = params.get('entry') ?? '';
    const selected = dossier?.rows.find((r)=>r.entryId === selectedId);
    const retry = (label, refetch)=>/*#__PURE__*/ _jsx(SpecraButton, {
            variant: "secondary",
            onClick: ()=>void refetch(),
            children: label
        });
    return /*#__PURE__*/ _jsx(RegistryRecordFrame, {
        kindLabel: "Labor Categories",
        tab: "lcats",
        title: dossier?.anchor?.title,
        status: status,
        error: queries.dossier.error,
        onRetry: ()=>void queries.dossier.refetch(),
        header: dossier?.anchor && /*#__PURE__*/ _jsx(AnchorHeader, {
            dossier: dossier,
            onMap: ()=>setMapOpen(true)
        }),
        railSections: dossier?.anchor ? anchorRail(dossier) : undefined,
        children: dossier?.anchor && /*#__PURE__*/ _jsxs("div", {
            className: "space-y-8",
            "data-testid": Testids.registryLcatAnchor.page,
            children: [
                /*#__PURE__*/ _jsx(VersionsTable, {
                    rows: dossier.rows,
                    selectedId: selectedId,
                    onSelect: (entryId)=>{
                        const next = new URLSearchParams(params);
                        next.set('entry', entryId);
                        setParams(next, {
                            replace: true
                        });
                    },
                    onMap: canMap ? ()=>setMapOpen(true) : undefined
                }),
                dossier.rows.length > 0 && /*#__PURE__*/ _jsx(AgainstTheAnchor, {
                    row: selected
                }),
                queries.points.isPending ? /*#__PURE__*/ _jsx(SpecraSpinner, {
                    label: "Loading rates"
                }) : queries.points.isError ? retry('Retry loading rates', queries.points.refetch) : /*#__PURE__*/ _jsx(RateDotPlot, {
                    data: queries.points.data,
                    anchorId: anchorId,
                    fy: fy,
                    currentFy: currentFy,
                    onFyChange: setFy
                }),
                queries.people.isPending ? /*#__PURE__*/ _jsx(SpecraSpinner, {
                    label: "Loading people"
                }) : queries.people.isError ? retry('Retry loading people', queries.people.refetch) : /*#__PURE__*/ _jsx(PeopleWhoFilledIt, {
                    people: queries.people.data.people
                }),
                canMap && /*#__PURE__*/ _jsx(MapLaborCategoryDialog, {
                    open: mapOpen,
                    onOpenChange: setMapOpen,
                    tenantId: user?.tenant_id ?? '',
                    anchorId: anchorId,
                    anchorTitle: dossier.anchor.title
                })
            ]
        })
    });
}
